import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

/** The session lives in httpOnly cookies (access + rotating refresh), so no
 * session token is ever readable by script. Cookie-authenticated writes are
 * protected by a session-bound CSRF token which we echo in `X-CSRF-Token`.
 *
 * The authoritative copy is the one the backend hands us in the login / refresh
 * / `GET /auth/csrf` response body, held in memory below. The readable
 * `csrf_token` cookie is only a fallback: inside a cross-site iframe (or with
 * partitioned cookies) `document.cookie` can legitimately come back empty, which
 * is what produced "CSRF token missing" on ingestion and analysis runs. */
let csrfMemory = "";

export function setCsrfToken(token) {
    if (token) csrfMemory = token;
}

function csrfCookie() {
    const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : "";
}

export function csrfToken() {
    return csrfMemory || csrfCookie();
}

/** Header bundle for the raw `fetch()` calls (SSE chat, audio blobs, multipart
 * uploads) that cannot go through the axios instance. */
export function authFetchInit(init = {}) {
    return {
        ...init,
        credentials: "include",
        headers: { ...(init.headers || {}), "X-CSRF-Token": csrfToken() },
    };
}

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
    if (!["get", "head", "options"].includes((config.method || "get").toLowerCase())) {
        config.headers["X-CSRF-Token"] = csrfToken();
    }
    // A multipart body must NOT inherit the instance's JSON content type: axios
    // silently converts FormData to a JSON object when the header says
    // application/json (axios/lib/defaults transformRequest), so the file never
    // leaves the browser and FastAPI answers 422. Dropping the header lets the
    // browser emit multipart/form-data with its own boundary.
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
        if (typeof config.headers?.delete === "function") config.headers.delete("Content-Type");
        else delete config.headers["Content-Type"];
    }
    return config;
});

/** FastAPI answers a validation failure with `detail` as an ARRAY of Pydantic
 * error objects. Call sites render `detail` straight into a toast, so an object
 * there crashes React ("Objects are not valid as a React child"). Flatten it
 * once, here at the boundary, so every call site always sees a string. */
function normalizeErrorDetail(response) {
    const data = response?.data;
    if (!data || typeof data !== "object" || typeof Blob !== "undefined" && data instanceof Blob) return;
    const detail = data.detail;
    if (Array.isArray(detail)) {
        data.detail =
            detail
                .map((e) => {
                    const loc = Array.isArray(e?.loc) ? e.loc.filter((p) => p !== "body").join(".") : "";
                    return [loc, e?.msg || e?.type].filter(Boolean).join(": ");
                })
                .filter(Boolean)
                .join(" · ") || "Request validation failed";
    } else if (detail && typeof detail === "object") {
        data.detail = detail.msg || detail.message || JSON.stringify(detail);
    }
}

// One shared refresh flight — a burst of 401s must not fire N refresh calls.
let refreshing = null;
const refreshSession = () => {
    if (!refreshing) {
        refreshing = api
            .post("/auth/refresh")
            .then((r) => { setCsrfToken(r?.data?.csrf_token); return r; })
            .finally(() => { refreshing = null; });
    }
    return refreshing;
};

// Same idea for CSRF recovery: one shared flight, then everyone retries.
let csrfFlight = null;
export const recoverCsrf = () => {
    if (!csrfFlight) {
        csrfFlight = api
            .get("/auth/csrf")
            .then((r) => { setCsrfToken(r?.data?.csrf_token); return r?.data?.csrf_token || ""; })
            .finally(() => { csrfFlight = null; });
    }
    return csrfFlight;
};

/** `fetch()` with the session headers, self-healing exactly once if the CSRF
 * token turns out to be stale (used by the SSE/multipart call sites). */
export async function authFetch(url, init = {}) {
    let res = await fetch(url, authFetchInit(init));
    if (res.status === 403 && !init._retried) {
        const body = await res.clone().text().catch(() => "");
        if (body.toLowerCase().includes("csrf")) {
            await recoverCsrf().catch(() => "");
            res = await fetch(url, authFetchInit({ ...init, _retried: true }));
        }
    }
    return res;
}

const AUTH_FREE = ["/auth/login", "/auth/refresh", "/auth/logout"];
const isCsrfFailure = (err) =>
    String(err?.response?.data?.detail || "").toLowerCase().includes("csrf");

api.interceptors.response.use(
    (r) => r,
    async (err) => {
        normalizeErrorDetail(err?.response);
        const status = err?.response?.status;
        const config = err?.config || {};
        const url = config.url || "";
        if (status === 401 && !config._retried && !AUTH_FREE.some((p) => url.includes(p))) {
            config._retried = true;
            try {
                await refreshSession();
                return api(config);
            } catch (e) {
                // `_silent` = the boot-time /auth/me probe; the router decides
                // where an anonymous visitor goes (public pages must not bounce).
                if (!config._silent && window.location.pathname !== "/login") {
                    window.location.href = "/login";
                }
                return Promise.reject(e);
            }
        }
        // A stale/unreadable CSRF token heals itself: fetch the session's token
        // and replay the write exactly once.
        if (status === 403 && isCsrfFailure(err) && !config._csrfRetried && !url.includes("/auth/csrf")) {
            config._csrfRetried = true;
            try {
                await recoverCsrf();
                return api(config);
            } catch (e) {
                return Promise.reject(err);
            }
        }
        if (status === 401 && !config._silent && window.location.pathname !== "/login" && !url.includes("/auth/")) {
            window.location.href = "/login";
        }
        return Promise.reject(err);
    }
);
